import React, { useState } from 'react';
import { X, Calendar, TrendingUp, TrendingDown, Minus, ArrowRight, BarChart3 } from 'lucide-react';
import axios from 'axios';

function ComparePeriods({ onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [comparison, setComparison] = useState(null);
  
  // Period selection state - use dates that match existing data
  const [period1, setPeriod1] = useState({
    start: '2024-01-01',
    end: '2024-01-15'
  });
  const [period2, setPeriod2] = useState({
    start: '2024-01-15',
    end: '2024-01-31'
  });

  function getDateString(daysAgo) {
    const date = new Date();
    date.setDate(date.getDate() - daysAgo);
    return date.toISOString().split('T')[0];
  }

  const compareScores = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Use midpoint of each period range for comparison
      const period1MidDate = new Date(period1.start);
      period1MidDate.setDate(period1MidDate.getDate() + Math.floor((new Date(period1.end) - new Date(period1.start)) / (1000 * 60 * 60 * 24 * 2)));
      
      const period2MidDate = new Date(period2.start);
      period2MidDate.setDate(period2MidDate.getDate() + Math.floor((new Date(period2.end) - new Date(period2.start)) / (1000 * 60 * 60 * 24 * 2)));
      
      const response = await axios.get('/scores/compare', {
        params: {
          period1: period1MidDate.toISOString(),
          period2: period2MidDate.toISOString()
        }
      });
      
      // Transform response to match our expected format
      const data = response.data;
      setComparison({
        period1: {
          // Use actual data period from backend for display
          start: data.period1?.period_start || period1.start,
          end: data.period1?.period_end || period1.end,
          requested_start: period1.start,
          requested_end: period1.end,
          scores: {
            overall: data.period1?.score || 0,
            authenticity: data.period1?.authenticity_score || 0,
            accountability: data.period1?.accountability_score || 0,
            community: data.period1?.community_score || 0
          },
          data_completeness: data.period1?.data_completeness || 0
        },
        period2: {
          // Use actual data period from backend for display
          start: data.period2?.period_start || period2.start,
          end: data.period2?.period_end || period2.end,
          requested_start: period2.start,
          requested_end: period2.end,
          scores: {
            overall: data.period2?.score || 0,
            authenticity: data.period2?.authenticity_score || 0,
            accountability: data.period2?.accountability_score || 0,
            community: data.period2?.community_score || 0
          },
          data_completeness: data.period2?.data_completeness || 0
        },
        deltas: {
          overall: {
            absolute_change: data.differences?.overall || 0,
            percent_change: data.percent_change?.overall || 0,
            direction: (data.differences?.overall || 0) > 0 ? 'up' : ((data.differences?.overall || 0) < 0 ? 'down' : 'stable')
          },
          authenticity: {
            absolute_change: data.differences?.authenticity || 0,
            percent_change: data.percent_change?.authenticity || 0,
            direction: (data.differences?.authenticity || 0) > 0 ? 'up' : ((data.differences?.authenticity || 0) < 0 ? 'down' : 'stable')
          },
          accountability: {
            absolute_change: data.differences?.accountability || 0,
            percent_change: data.percent_change?.accountability || 0,
            direction: (data.differences?.accountability || 0) > 0 ? 'up' : ((data.differences?.accountability || 0) < 0 ? 'down' : 'stable')
          },
          community: {
            absolute_change: data.differences?.community || 0,
            percent_change: data.percent_change?.community || 0,
            direction: (data.differences?.community || 0) > 0 ? 'up' : ((data.differences?.community || 0) < 0 ? 'down' : 'stable')
          }
        }
      });
    } catch (err) {
      console.error('Comparison error:', err);
      setError(err.response?.data?.detail || 'Failed to compare periods. Make sure both periods have calculated scores.');
    } finally {
      setLoading(false);
    }
  };

  const getDeltaIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-5 h-5 text-green-500" />;
      case 'down':
        return <TrendingDown className="w-5 h-5 text-red-500" />;
      default:
        return <Minus className="w-5 h-5 text-gray-400" />;
    }
  };

  const getDeltaColor = (direction) => {
    switch (direction) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-500';
    }
  };

  const formatDate = (dateStr) => {
    // Parse date string directly to avoid timezone conversion issues
    // Format: "YYYY-MM-DD" -> extract parts and format manually
    if (!dateStr) return 'N/A';
    
    const parts = dateStr.split('-');
    if (parts.length !== 3) {
      // Fallback for non-standard date formats
      return dateStr;
    }
    
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // JavaScript months are 0-indexed
    const day = parseInt(parts[2], 10);
    
    // Create date using local timezone (not UTC)
    const date = new Date(year, month, day);
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const pillars = [
    { key: 'overall', name: 'Trust Score', color: 'bg-purple-500', textColor: 'text-purple-600' },
    { key: 'community', name: 'Community', color: 'bg-blue-500', textColor: 'text-blue-600' },
    { key: 'authenticity', name: 'Authenticity', color: 'bg-violet-500', textColor: 'text-violet-600' },
    { key: 'accountability', name: 'Accountability', color: 'bg-green-500', textColor: 'text-green-600' }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="compare-periods-modal">
      <div className="bg-white rounded-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-2xl font-bold mb-1 flex items-center gap-2">
                <BarChart3 className="w-6 h-6" />
                Compare Periods
              </h2>
              <p className="text-white/80">Compare Trust Scores between two time periods</p>
            </div>
            <button 
              onClick={onClose} 
              className="text-white/80 hover:text-white transition"
              data-testid="close-compare"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 150px)' }}>
          {/* Period Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Period 1 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Period 1 (Baseline)
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Start Date</label>
                  <input
                    type="date"
                    value={period1.start}
                    onChange={(e) => setPeriod1({ ...period1, start: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="period1-start"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-500">End Date</label>
                  <input
                    type="date"
                    value={period1.end}
                    onChange={(e) => setPeriod1({ ...period1, end: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="period1-end"
                  />
                </div>
              </div>
            </div>

            {/* Period 2 */}
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Period 2 (Current)
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="text-sm text-gray-500">Start Date</label>
                  <input
                    type="date"
                    value={period2.start}
                    onChange={(e) => setPeriod2({ ...period2, start: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="period2-start"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-500">End Date</label>
                  <input
                    type="date"
                    value={period2.end}
                    onChange={(e) => setPeriod2({ ...period2, end: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="period2-end"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Compare Button */}
          <button
            onClick={compareScores}
            disabled={loading}
            className="w-full py-3 px-6 bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-semibold rounded-xl hover:from-purple-700 hover:to-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="compare-button"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Comparing...
              </>
            ) : (
              <>
                Compare Periods
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="compare-error">
              {error}
            </div>
          )}

          {/* Comparison Results */}
          {comparison && (
            <div className="mt-8" data-testid="comparison-results">
              {/* Period Headers - Always show user's selected dates */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div></div>
                <div className="text-center">
                  <div className="bg-gray-100 rounded-lg px-4 py-2">
                    <p className="text-sm text-gray-500">Period 1</p>
                    <p className="font-medium text-gray-900" data-testid="period1-display-dates">
                      {formatDate(comparison.period1.requested_start)} - {formatDate(comparison.period1.requested_end)}
                    </p>
                  </div>
                </div>
                <div className="text-center">
                  <div className="bg-purple-100 rounded-lg px-4 py-2">
                    <p className="text-sm text-purple-600">Period 2</p>
                    <p className="font-medium text-gray-900" data-testid="period2-display-dates">
                      {formatDate(comparison.period2.requested_start)} - {formatDate(comparison.period2.requested_end)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Score Comparison Cards */}
              <div className="space-y-4">
                {pillars.map((pillar) => {
                  const score1 = comparison.period1.scores[pillar.key];
                  const score2 = comparison.period2.scores[pillar.key];
                  const delta = comparison.deltas[pillar.key];
                  
                  return (
                    <div 
                      key={pillar.key}
                      className={`grid grid-cols-3 gap-4 items-center p-4 rounded-xl ${pillar.key === 'overall' ? 'bg-purple-50 border-2 border-purple-200' : 'bg-gray-50'}`}
                      data-testid={`comparison-${pillar.key}`}
                    >
                      {/* Pillar Name */}
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${pillar.color}`}></div>
                        <span className={`font-semibold ${pillar.key === 'overall' ? 'text-lg' : ''} text-gray-900`}>
                          {pillar.name}
                        </span>
                      </div>

                      {/* Period 1 Score */}
                      <div className="text-center">
                        <span className={`text-2xl font-bold ${pillar.key === 'overall' ? 'text-3xl' : ''} text-gray-700`}>
                          {score1?.toFixed(1) || '—'}
                        </span>
                      </div>

                      {/* Period 2 Score + Delta */}
                      <div className="text-center">
                        <span className={`text-2xl font-bold ${pillar.key === 'overall' ? 'text-3xl' : ''} ${pillar.textColor}`}>
                          {score2?.toFixed(1) || '—'}
                        </span>
                        <div className={`flex items-center justify-center gap-1 mt-1 ${getDeltaColor(delta.direction)}`}>
                          {getDeltaIcon(delta.direction)}
                          <span className="font-semibold">
                            {delta.absolute_change > 0 ? '+' : ''}{delta.absolute_change.toFixed(1)}
                          </span>
                          <span className="text-sm">
                            ({delta.percent_change > 0 ? '+' : ''}{delta.percent_change.toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Data Completeness Comparison */}
              <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-gray-500 mb-1">Data Completeness (Period 1)</p>
                  <p className="text-2xl font-bold text-gray-700">{comparison.period1.data_completeness?.toFixed(1) || 0}%</p>
                </div>
                <div className="bg-purple-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-purple-600 mb-1">Data Completeness (Period 2)</p>
                  <p className="text-2xl font-bold text-purple-700">{comparison.period2.data_completeness?.toFixed(1) || 0}%</p>
                </div>
              </div>

              {/* Summary Insight */}
              <div className="mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                <h4 className="font-semibold text-indigo-900 mb-2">Summary</h4>
                <p className="text-gray-700">
                  {comparison.deltas.overall.direction === 'up' ? (
                    <>
                      Your Trust Score <span className="text-green-600 font-semibold">improved by {comparison.deltas.overall.absolute_change.toFixed(1)} points</span> ({comparison.deltas.overall.percent_change.toFixed(1)}%) between the two periods.
                      {comparison.deltas.overall.absolute_change >= 5 && ' Great progress!'}
                    </>
                  ) : comparison.deltas.overall.direction === 'down' ? (
                    <>
                      Your Trust Score <span className="text-red-600 font-semibold">declined by {Math.abs(comparison.deltas.overall.absolute_change).toFixed(1)} points</span> ({Math.abs(comparison.deltas.overall.percent_change).toFixed(1)}%) between the two periods.
                      Consider reviewing the pillar breakdown to identify areas for improvement.
                    </>
                  ) : (
                    <>
                      Your Trust Score remained <span className="text-gray-600 font-semibold">stable</span> between the two periods.
                    </>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ComparePeriods;
